/**
 * `<caos-open-records>` — the strip of records you currently have OPEN, under the app navigation.
 * (Catalog: Navigation › OpenRecords — the strip of records you currently have OPEN, under the app navigation.)
 *
 * Opening a record does not throw away the one you were looking at: each opened record earns a small
 * entry here, with its own close control, and the strip survives moving between list views — so a
 * user can hold several records open at once and come back to any of them. This is the CAOS port of
 * the reference workspace's record-tab strip.
 *
 * The strip owns NO state: it renders the `records` property and emits what the user did. The shell
 * owns which records are open and which one you are looking at, because that is a routing concern.
 * An EMPTY list renders nothing at all (`:host(:not([has-records]))` collapses), so a workspace with
 * nothing open shows no empty rail.
 *
 * Data in (a PROPERTY — arrays cannot ride attributes):
 *   `records` — `{ id, label, href, active? }[]`. `label` is what the entry reads; the reference
 *               shape is `<short code> · <record's bare identifying value>` (CAOS-386 — e.g.
 *               "INV · 1042", not the object spelled out), composed by the caller (this element
 *               never invents an abbreviation for an object it knows nothing about). The entry
 *               button is capped at 22ch with an ellipsis, which is exactly why the caller keeps
 *               the code short: a long spelled-out label is what used to eat that budget and
 *               truncate the one part — the record's own number — that actually distinguishes it.
 *
 * Events out (bubbling + composed — the host owns navigation and the open set):
 *   `record-open`  — detail `{ id, href }`, an entry was chosen.
 *   `record-close` — detail `{ id, href }`, an entry's close control was used.
 *
 * Keyboard: a roving tabindex across the entries (Arrow Left/Right/Home/End), matching
 * `<caos-tab-strip>`; each entry's close control is reachable with Tab from the entry itself.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

export interface CaosOpenRecord {
  id: string;
  label: string;
  href: string;
  active?: boolean;
}

const sheet = makeSheet(`
  :host { display: block; }
  /* Nothing open ⇒ no strip at all (never an empty rail taking vertical space). */
  :host(:not([has-records])) { display: none; }

  .strip {
    display: flex;
    align-items: stretch;
    gap: 0;
    max-width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    border-bottom: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface);
  }
  .strip::-webkit-scrollbar { display: none; }

  /* One open record: its label button and its close control read as a single unit, separated from
     the next by a hairline (the reference's thin divided rail). */
  .entry {
    display: inline-flex;
    align-items: center;
    gap: var(--caos-space-2);
    padding: 0 var(--caos-space-3);
    border-right: 1px solid var(--caos-color-border);
    white-space: nowrap;
  }

  .open {
    appearance: none;
    border: none;
    background: transparent;
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font-mono);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.08em;
    line-height: 1;
    padding: var(--caos-space-3) 0;
    cursor: pointer;
    max-width: 22ch;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 0.12s ease;
  }
  .open:hover { color: var(--caos-color-text); }
  .entry.is-active .open { color: var(--caos-color-text); font-weight: 700; }
  .entry.is-active { box-shadow: inset 0 -2px 0 0 var(--caos-color-accent); }

  .close {
    appearance: none;
    border: none;
    background: transparent;
    color: var(--caos-color-text-subtle, var(--caos-color-text-muted));
    display: inline-flex;
    align-items: center;
    padding: 2px;
    cursor: pointer;
    transition: color 0.12s ease;
  }
  .close:hover { color: var(--caos-color-text); }
  .close svg { width: 11px; height: 11px; display: block; }

  .open:focus-visible, .close:focus-visible {
    outline: none;
    border-radius: var(--caos-radius-sm);
    box-shadow: var(--caos-focus-ring);
  }
`);

export class CaosOpenRecords extends CaosElement {
  private strip: HTMLDivElement;
  private _records: CaosOpenRecord[] = [];
  /** The label buttons, in render order — the roving-tabindex ring. */
  private openButtons: HTMLButtonElement[] = [];

  constructor() {
    super(sheet);
    this.strip = document.createElement('div');
    this.strip.className = 'strip';
    this.strip.setAttribute('part', 'strip');
    this.strip.addEventListener('keydown', (e) => this.onKeydown(e));
    this.root.append(this.strip);
  }

  connectedCallback(): void {
    if (!this.hasAttribute('aria-label')) this.setAttribute('aria-label', 'Open records');
    this.render();
  }

  /** The records currently open, in the order they were opened. */
  get records(): CaosOpenRecord[] {
    return this._records;
  }
  set records(list: CaosOpenRecord[]) {
    this._records = Array.isArray(list) ? list : [];
    this.render();
  }

  private render(): void {
    this.strip.replaceChildren();
    this.openButtons = [];
    // The host carries the presence flag so an empty set collapses in CSS rather than in script.
    if (this._records.length > 0) this.setAttribute('has-records', '');
    else this.removeAttribute('has-records');

    for (const r of this._records) {
      const entry = document.createElement('div');
      entry.className = r.active ? 'entry is-active' : 'entry';

      const open = document.createElement('button');
      open.type = 'button';
      open.className = 'open';
      open.textContent = r.label;
      open.title = r.label;
      if (r.active) open.setAttribute('aria-current', 'page');
      open.tabIndex = r.active ? 0 : -1;
      open.addEventListener('click', () => this.emit('record-open', { id: r.id, href: r.href }));

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'close';
      close.setAttribute('aria-label', `Close ${r.label}`);
      close.title = `Close ${r.label}`;
      close.append(makeIcon('close'));
      close.addEventListener('click', (e) => {
        e.stopPropagation();
        this.emit('record-close', { id: r.id, href: r.href });
      });

      entry.append(open, close);
      this.strip.append(entry);
      this.openButtons.push(open);
    }
    // Nothing marked active (you are on a list, with records still open) ⇒ the first entry carries
    // the tab stop, so the strip is always reachable by keyboard.
    if (this.openButtons.length > 0 && !this._records.some((r) => r.active)) {
      this.openButtons[0]!.tabIndex = 0;
    }
  }

  private onKeydown(e: KeyboardEvent): void {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key) || this.openButtons.length === 0) return;
    const current = this.openButtons.indexOf(document.activeElement as HTMLButtonElement);
    const from = current < 0 ? 0 : current;
    let next = from;
    if (e.key === 'ArrowLeft') next = (from - 1 + this.openButtons.length) % this.openButtons.length;
    else if (e.key === 'ArrowRight') next = (from + 1) % this.openButtons.length;
    else if (e.key === 'Home') next = 0;
    else next = this.openButtons.length - 1;
    e.preventDefault();
    for (const b of this.openButtons) b.tabIndex = -1;
    const target = this.openButtons[next]!;
    target.tabIndex = 0;
    target.focus();
  }

  private emit(type: string, detail: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
  }
}

define('caos-open-records', CaosOpenRecords);

declare global {
  interface HTMLElementTagNameMap {
    'caos-open-records': CaosOpenRecords;
  }
}
